import React, { useState, useEffect, useRef } from 'react';
import { 
  Search, LocateFixed, Loader2, AlertTriangle, RefreshCcw
} from 'lucide-react';

class MapErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  render() {
    if (this.state.hasError) {
      return (
        <div className="h-full w-full bg-[#051320] flex items-center justify-center p-8 text-center text-white font-sans">
          <div className="max-w-md">
            <AlertTriangle className="mx-auto text-red-500 mb-4" size={48} />
            <h2 className="text-xl font-bold mb-2">حدث خطأ في عرض الخريطة</h2>
            <p className="text-gray-400 text-sm mb-6">{this.state.error?.message}</p>
            <button onClick={() => window.location.reload()} className="flex items-center gap-2 mx-auto bg-blue-600 px-4 py-2 rounded-lg hover:bg-blue-500 transition-colors">
              <RefreshCcw size={18} /> تحديث الصفحة
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const MapAreaContent = ({ mode, onConfirm, onCancel, activeCases, hotspots }) => {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markerRef = useRef(null); 
  const isMounted = useRef(true);
  const govLayerRef = useRef(null);
  const hotspotsLayerRef = useRef(null); 
  const searchTimeoutRef = useRef(null); 

  const [isLeafletReady, setIsLeafletReady] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [loading, setLoading] = useState(true);
  const [statusMsg, setStatusMsg] = useState("جاري جلب محرك الخرائط...");
  const [clickedCoord, setClickedCoord] = useState(null);
  const [isSearching, setIsSearching] = useState(false); 

  const styles = {
    admin1: { color: "#0ea5e9", weight: 2, fillOpacity: 0.1, fillColor: "#0ea5e9" },
  };

  useEffect(() => {
    isMounted.current = true;
    if (window.L) { setIsLeafletReady(true); return; }

    const existingScript = document.querySelector('script[src*="leaflet.js"]');
    if (!existingScript) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);

      const script = document.createElement('script');
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.async = true;
      script.onload = () => { if (isMounted.current) setIsLeafletReady(true); };
      document.body.appendChild(script);
    } else {
      existingScript.addEventListener('load', () => { if (isMounted.current) setIsLeafletReady(true); });
      if (window.L && isMounted.current) setIsLeafletReady(true);
    }
    return () => { isMounted.current = false; };
  }, []);

  useEffect(() => {
    if (!isLeafletReady || !mapRef.current || mapInstance.current) return;

    const L = window.L;
    if (!L) return; 

    const map = L.map(mapRef.current, { zoomControl: false, attributionControl: false }).setView([34.8, 38.5], 7);
    mapInstance.current = map;

    const streetMap = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png').addTo(map);
    const satelliteMap = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}');

    L.control.zoom({ position: 'bottomleft' }).addTo(map);

    map.on('click', (e) => {
      const { lat, lng } = e.latlng;
      handleSelectLocation(lat, lng);
    });

    const loadInitialData = async () => {
      setStatusMsg("جاري تحميل التقسيمات الإدارية...");
      const overlays = {};

      try {
        const resGov = await fetch(`/data/syr_admin1.geojson`);
        if (resGov.ok) {
           const dataGov = await resGov.json();
           const govLayer = L.geoJSON(dataGov, {
             style: styles.admin1,
             onEachFeature: (f, l) => {
               const name = f.properties.adm1_name1 || f.properties.name_ar || f.properties.name || "محافظة غير مسماة";
               l.feature.properties.arabicName = name; 
               if (mode !== 'picker') l.bindPopup(`<b>المحافظة:</b> ${name}`);
             }
           });
           overlays['المحافظات'] = govLayer;
           govLayerRef.current = govLayer; 
           govLayer.addTo(map);
        }
      } catch (e) { console.error("Error loading geojson files", e); }

      const baseMaps = { "خريطة الشوارع": streetMap, "الأقمار الصناعية": satelliteMap };
      L.control.layers(baseMaps, overlays, { position: 'topright' }).addTo(map);
      
      if (isMounted.current) setLoading(false);
    };

    loadInitialData();

    return () => { 
      if (mapInstance.current) {
        mapInstance.current.remove();
        mapInstance.current = null;
      }
    };
  }, [isLeafletReady, mode]);

  const handleSelectLocation = (lat, lng) => {
    const L = window.L;
    if (!L || !mapInstance.current) return;

    setClickedCoord({ lat, lng });

    const customIcon = L.divIcon({
        className: 'custom-map-marker',
        html: `<div style="background-color: #ef4444; width: 18px; height: 18px; border-radius: 50%; border: 2px solid white; box-shadow: 0 0 10px rgba(0,0,0,0.5);"></div>`,
        iconSize: [18, 18],
        iconAnchor: [9, 9]
    });

    if (mode === 'picker') {
      if (markerRef.current) mapInstance.current.removeLayer(markerRef.current);
      markerRef.current = L.marker([lat, lng], { icon: customIcon }).addTo(mapInstance.current);
    } else {
      L.popup().setLatLng([lat, lng]).setContent(`
        <div class="text-right p-1 font-sans">
          <div class="font-bold text-gray-800 border-b mb-1 pb-1">إحداثيات الموقع</div>
          <div class="text-xs text-gray-600 font-mono select-all mb-2">${lat.toFixed(6)}, ${lng.toFixed(6)}</div>
          <button class="w-full text-xs bg-blue-600 hover:bg-blue-700 text-white px-2 py-1.5 rounded transition-colors" onclick="navigator.clipboard.writeText('${lat},${lng}')">نسخ الإحداثيات</button>
        </div>
      `).openOn(mapInstance.current);
    }
  };


  // [تعديل] رسم بؤر الذكاء الصنعي فقط (أخضر، أصفر، أحمر) وتجاهل الحالات الفردية
  useEffect(() => {
    const L = window.L;
    if (!L || !mapInstance.current || !hotspots || hotspots.length === 0) return;

    if (hotspotsLayerRef.current) {
      hotspotsLayerRef.current.clearLayers();
    } else {
      hotspotsLayerRef.current = L.featureGroup().addTo(mapInstance.current);
    }

    hotspots.forEach(cluster => {
      // تجاهل البؤر التي لم يتم تحديد إحداثياتها بشكل صحيح (0, 0)
      if (cluster.center_lat === 0 && cluster.center_long === 0) return;

      // الإعدادات الافتراضية (منخفضة الخطورة - خضراء)
      let color = '#22c55e'; 
      let customClass = 'safe-pulse-circle';
      let titlePrefix = 'بؤرة منخفضة الخطورة';
      let titleColor = 'text-green-500';
      let riskLevelText = cluster.risk_level || "غير محدد";
      let diseaseName = cluster.disease_name || "مرض غير محدد";

      if (cluster.risk_level > 7) {
        // عالية الخطورة - حمراء
        color = '#ef4444'; 
        customClass = 'danger-pulse-circle';
        titlePrefix = '⚠️ تنبؤ بوباء (عالية الخطورة)';
        titleColor = 'text-red-600';
      } else if (cluster.risk_level > 4) {
        // متوسطة الخطورة - صفراء
        color = '#eab308'; 
        customClass = 'warning-pulse-circle';
        titlePrefix = '⚠️ بؤرة متوسطة الخطورة';
        titleColor = 'text-yellow-500';
      }
      
      // رسم الدائرة
      L.circle([cluster.center_lat, cluster.center_long], {
        color: color,
        fillColor: color,
        fillOpacity: 0.6,
        weight: 3,        
        className: customClass, 
        radius: (cluster.radius || 5) * 1000 // افتراض 5 كم إن لم يتوفر نصف القطر
      }).addTo(hotspotsLayerRef.current)
        .bindPopup(`
          <div class="text-right font-sans min-w-[220px]">
            <div class="font-bold text-lg mb-2 pb-1 border-b ${titleColor}">${titlePrefix}</div>
            <div class="flex justify-between text-sm mb-1"><span>المرض:</span> <b class="text-gray-800 font-mono">${diseaseName}</b></div>
            <div class="flex justify-between text-sm mb-1"><span>الحالات المكتشفة:</span> <b class="text-gray-800 font-mono">${cluster.case_count || 0}</b></div>
            <div class="flex justify-between text-sm"><span>مؤشر الخطورة:</span> <b style="color:${color}" class="font-mono">${riskLevelText}/10</b></div>
          </div>
        `);
    });
  }, [hotspots, isLeafletReady]);


  const handleSearch = (val) => {
    setQuery(val);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    if (val.trim().length < 3) {
      setResults([]);
      setShowResults(false);
      return;
    }

    setIsSearching(true);
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(val.trim())}&accept-language=ar&countrycodes=sy&limit=10`;
        const response = await fetch(url, {
          headers: { 'User-Agent': 'Epidemic-Prediction-Platform-Syria' }
        });
        
        if (response.ok) {
          const data = await response.json();
          const mapped = data.map(item => {
            const parts = item.display_name.split(',');
            const shortName = parts[0]; 

            let placeType = "مكان";
            if (item.class === 'amenity' && item.type === 'school') placeType = "مدرسة";
            else if (item.class === 'amenity' && item.type === 'place_of_worship') placeType = "مدرسة/مسجد/كنيسة";
            else if (item.class === 'boundary' || item.type === 'administrative') placeType = "حي/منطقة";
            else if (item.class === 'highway') placeType = "شارع";

            return {
              name: shortName,
              fullName: item.display_name,
              type: placeType,
              lat: parseFloat(item.lat),
              lng: parseFloat(item.lon)
            };
          });
          setResults(mapped);
          setShowResults(true);
        }
      } catch (err) {
        console.error("Geocoding API Fetch error:", err);
      } finally {
        setIsSearching(false);
      }
    }, 600);
  };

  const handleSelectResult = (res) => {
    if (!mapInstance.current) return;
    mapInstance.current.setView([res.lat, res.lng], 15);
    handleSelectLocation(res.lat, res.lng);
    setQuery(res.name);
    setShowResults(false);
  };

  return (
    <div className="relative w-full h-full bg-[#051320] font-sans overflow-hidden rounded-xl">
      <style>{`
        /* تأثيرات الوميض حسب درجة الخطورة */
        .danger-pulse-circle path {
          animation: map-pulse-danger 1.5s infinite alternate;
          filter: drop-shadow(0 0 12px rgba(239, 68, 68, 0.9));
        }
        .warning-pulse-circle path {
          animation: map-pulse-warning 1.8s infinite alternate;
          filter: drop-shadow(0 0 10px rgba(234, 179, 8, 0.8));
        }
        .safe-pulse-circle path {
          animation: map-pulse-safe 2s infinite alternate;
          filter: drop-shadow(0 0 8px rgba(34, 197, 94, 0.8));
        }
        
        @keyframes map-pulse-danger {
          0% { stroke-width: 2.5px; fill-opacity: 0.45; stroke-opacity: 0.7; }
          100% { stroke-width: 6.5px; fill-opacity: 0.75; stroke-opacity: 1; }
        }
        @keyframes map-pulse-warning {
          0% { stroke-width: 2px; fill-opacity: 0.35; stroke-opacity: 0.6; }
          100% { stroke-width: 5.5px; fill-opacity: 0.7; stroke-opacity: 0.95; }
        }
        @keyframes map-pulse-safe {
          0% { stroke-width: 1.5px; fill-opacity: 0.3; stroke-opacity: 0.5; }
          100% { stroke-width: 4px; fill-opacity: 0.6; stroke-opacity: 0.9; }
        }
      `}</style>

      {loading && (
        <div className="absolute inset-0 z-[2000] flex flex-col items-center justify-center bg-[#051320] text-white">
          <Loader2 className="animate-spin mb-4 text-blue-500" size={48} />
          <p className="text-lg animate-pulse">{statusMsg}</p>
        </div>
      )}

      <div className="absolute top-6 left-1/2 -translate-x-1/2 z-[1000] w-full max-w-lg px-4 pointer-events-none">
        <div className="relative pointer-events-auto">
          <div className="bg-[#0a1f33]/95 backdrop-blur-md border border-white/10 rounded-2xl flex items-center px-4 py-3 shadow-2xl">
            {isSearching ? (
              <Loader2 size={20} className="text-blue-400 ml-3 animate-spin" />
            ) : (
              <Search size={20} className="text-blue-400 ml-3" />
            )}
            <input
              type="text" placeholder="ابحث عن مدرسة، مسجد، حي، أو أي مكان مسجل..."
              className="bg-transparent border-none outline-none flex-grow text-white text-right text-sm placeholder-gray-500"
              value={query} onChange={(e) => handleSearch(e.target.value)} onFocus={() => query.length >= 3 && setShowResults(true)} dir="rtl"
            />
          </div>
          
          {showResults && results.length > 0 && (
            <div className="absolute top-full mt-2 w-full bg-[#0a1f33] border border-white/10 rounded-xl overflow-hidden shadow-2xl max-h-[45vh] overflow-y-auto">
              {results.map((res, i) => (
                <div key={i} className="p-3 hover:bg-blue-600/30 cursor-pointer border-b border-white/5 text-right flex justify-between items-center" onMouseDown={() => handleSelectResult(res)}>
                  <span className="text-[10px] px-2 py-0.5 rounded uppercase font-bold border shrink-0 ml-2 bg-blue-900 text-blue-300 border-blue-700">{res.type}</span>
                  <div className="flex flex-col text-right">
                    <span className="text-sm text-white font-medium">{res.name}</span>
                    <span className="text-[11px] text-gray-400 truncate max-w-[320px]">{res.fullName}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {mode === 'picker' && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-[2000] flex gap-4 w-full justify-center pointer-events-none">
          <button 
            type="button"
            onClick={() => clickedCoord ? onConfirm(clickedCoord.lat, clickedCoord.lng) : alert("الرجاء النقر على الخريطة أو البحث لتحديد الموقع أولاً")}
            className="bg-green-600 hover:bg-green-500 text-white font-bold py-3 px-8 rounded-full shadow-lg transition-transform transform hover:scale-105 pointer-events-auto"
          >
            موافق (اعتماد الموقع)
          </button>
          <button 
            type="button"
            onClick={onCancel}
            className="bg-red-600 hover:bg-red-500 text-white font-bold py-3 px-8 rounded-full shadow-lg transition-transform transform hover:scale-105 pointer-events-auto"
          >
            إلغاء
          </button>
        </div>
      )}

      <div className="absolute bottom-6 right-6 z-[1000] flex flex-col gap-3">
        <button type="button" onClick={() => navigator.geolocation.getCurrentPosition(p => mapInstance.current.setView([p.coords.latitude, p.coords.longitude], 14))} className="bg-[#0a1f33] p-3 rounded-full shadow-2xl text-blue-400 border border-blue-500/50 hover:bg-blue-900/50 transition-all pointer-events-auto">
          <LocateFixed size={24} />
        </button>
      </div>

      <div ref={mapRef} className="w-full h-full z-0 outline-none bg-[#051320]"></div>
    </div>
  );
};

const MapArea = (props) => (
  <MapErrorBoundary>
    <MapAreaContent {...props} />
  </MapErrorBoundary>
);

export default MapArea;